import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Target,
  Eye,
  Filter
} from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Button } from '@shared/components/ui/Button';

interface QualityScore {
  id: string;
  job_match_id: string;
  job_analysis_id: string;
  overall_score: number;
  rubric_scores: {
    score_justification: number;
    content_completeness: number;
    actionable_guidance: number;
    logical_consistency: number;
  };
  validation_passed: boolean;
  validation_errors: string[];
  validation_warnings: string[];
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  job_match: {
    job_title: string;
    company_name: string;
    match_score: number;
    user: {
      full_name: string;
      email: string;
    };
  };
  job_analysis: {
    strengths: string[];
    weaknesses: string[];
  };
}

type FilterType = 'all' | 'failed' | 'warnings' | 'passed';

const AdminJobMatchQuality: React.FC = () => {
  const navigate = useNavigate();
  const [qualityScores, setQualityScores] = useState<QualityScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedScore, setSelectedScore] = useState<QualityScore | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    passed: 0,
    failed: 0,
    warnings: 0,
    averageScore: 0
  });

  useEffect(() => {
    loadQualityScores();
  }, [filter]);

  const loadQualityScores = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('analysis_quality_scores')
        .select(`
          *,
          job_match:job_matches!analysis_quality_scores_job_match_id_fkey (
            job_title,
            company_name,
            match_score,
            user:profiles!job_matches_user_id_fkey (
              full_name
            )
          ),
          job_analysis:job_analysis!analysis_quality_scores_job_analysis_id_fkey (
            strengths,
            weaknesses
          )
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (filter === 'failed') {
        query = query.eq('validation_passed', false);
      } else if (filter === 'warnings') {
        query = query.neq('validation_warnings', '[]');
      } else if (filter === 'passed') {
        query = query.eq('validation_passed', true);
      }

      const { data, error } = await query;

      if (error) throw error;

      setQualityScores(data || []);

      // Calculate stats
      const total = data?.length || 0;
      const passed = data?.filter(s => s.validation_passed).length || 0;
      const failed = data?.filter(s => !s.validation_passed).length || 0;
      const warnings = data?.filter(s => s.validation_warnings && s.validation_warnings.length > 0).length || 0;
      const avgScore = total > 0 ? Math.round(data.reduce((sum, s) => sum + s.overall_score, 0) / total) : 0;

      setStats({
        total,
        passed,
        failed,
        warnings,
        averageScore: avgScore
      });
    } catch (error: any) {
      console.error('Error loading quality scores:', error);
      toast.error('Failed to load quality scores');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Good';
    if (score >= 60) return 'Fair';
    return 'Poor';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="ghost"
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Admin Dashboard
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Job Match Quality Control
              </h1>
              <p className="text-gray-600">
                Monitor and review AI analysis quality scores
              </p>
            </div>
            <Button onClick={loadQualityScores} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-border/40 p-4">
            <div className="text-sm text-gray-600 mb-1">Total Analyses</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <div className="text-sm text-green-700 mb-1">Passed</div>
            <div className="text-2xl font-bold text-green-900">{stats.passed}</div>
          </div>
          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
            <div className="text-sm text-red-700 mb-1">Failed</div>
            <div className="text-2xl font-bold text-red-900">{stats.failed}</div>
          </div>
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <div className="text-sm text-yellow-700 mb-1">Warnings</div>
            <div className="text-2xl font-bold text-yellow-900">{stats.warnings}</div>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <div className="text-sm text-blue-700 mb-1">Avg Quality</div>
            <div className="text-2xl font-bold text-blue-900">{stats.averageScore}%</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-border/40 p-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <span className="font-medium text-gray-900">Filter:</span>
            <div className="flex gap-2">
              {[
                { value: 'all' as FilterType, label: 'All' },
                { value: 'failed' as FilterType, label: 'Failed Only' },
                { value: 'warnings' as FilterType, label: 'With Warnings' },
                { value: 'passed' as FilterType, label: 'Passed Only' }
              ].map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setFilter(value)}
                  className={`
                    px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${filter === value
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }
                  `}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Quality Scores List */}
        <div className="bg-white rounded-xl border border-border/40">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Match
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Match Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quality Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issues
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      Loading quality scores...
                    </td>
                  </tr>
                ) : qualityScores.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                      No quality scores found
                    </td>
                  </tr>
                ) : (
                  qualityScores.map((score) => (
                    <tr key={score.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {score.job_match?.job_title || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {score.job_match?.company_name || 'Unknown Company'}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {score.job_match?.user?.full_name || 'Unknown User'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-gray-900">
                          {score.job_match?.match_score || 0}%
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(score.overall_score)}`}>
                          {score.overall_score}% {getScoreLabel(score.overall_score)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {score.validation_passed ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="text-sm">Passed</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-red-600">
                            <XCircle className="h-4 w-4" />
                            <span className="text-sm">Failed</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {score.validation_errors && score.validation_errors.length > 0 && (
                            <div className="flex items-center gap-1 text-red-600 text-xs">
                              <XCircle className="h-3 w-3" />
                              {score.validation_errors.length} error(s)
                            </div>
                          )}
                          {score.validation_warnings && score.validation_warnings.length > 0 && (
                            <div className="flex items-center gap-1 text-yellow-600 text-xs">
                              <AlertTriangle className="h-3 w-3" />
                              {score.validation_warnings.length} warning(s)
                            </div>
                          )}
                          {(!score.validation_errors || score.validation_errors.length === 0) &&
                           (!score.validation_warnings || score.validation_warnings.length === 0) && (
                            <span className="text-xs text-gray-400">None</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(score.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          onClick={() => {
                            setSelectedScore(score);
                            setShowDetailModal(true);
                          }}
                          variant="ghost"
                          size="sm"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedScore && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Quality Score Details
                  </h2>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
                <p className="text-gray-600">
                  {selectedScore.job_match?.job_title} at {selectedScore.job_match?.company_name}
                </p>
              </div>

              <div className="p-6 space-y-6">
                {/* Overall Score */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Overall Quality Score</h3>
                  <div className={`inline-flex items-center px-4 py-2 rounded-lg text-lg font-bold ${getScoreColor(selectedScore.overall_score)}`}>
                    {selectedScore.overall_score}/100 - {getScoreLabel(selectedScore.overall_score)}
                  </div>
                </div>

                {/* Rubric Breakdown */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Rubric Scores Breakdown</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(selectedScore.rubric_scores).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-4">
                        <div className="text-sm text-gray-600 mb-1">
                          {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </div>
                        <div className="text-xl font-bold text-gray-900">{value}/25</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Validation Errors */}
                {selectedScore.validation_errors && selectedScore.validation_errors.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                      <XCircle className="h-5 w-5" />
                      Validation Errors
                    </h3>
                    <ul className="space-y-2">
                      {selectedScore.validation_errors.map((error, index) => (
                        <li key={index} className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Validation Warnings */}
                {selectedScore.validation_warnings && selectedScore.validation_warnings.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5" />
                      Validation Warnings
                    </h3>
                    <ul className="space-y-2">
                      {selectedScore.validation_warnings.map((warning, index) => (
                        <li key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
                          {warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Analysis Preview */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-green-900 mb-2">Strengths ({selectedScore.job_analysis?.strengths?.length || 0})</h3>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 max-h-40 overflow-y-auto">
                      {selectedScore.job_analysis?.strengths?.length ? (
                        <ul className="list-disc list-inside space-y-1">
                          {selectedScore.job_analysis.strengths.map((s, i) => (
                            <li key={i}>{s.substring(0, 100)}{s.length > 100 ? '...' : ''}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500">No strengths listed</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-2">Weaknesses ({selectedScore.job_analysis?.weaknesses?.length || 0})</h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700 max-h-40 overflow-y-auto">
                      {selectedScore.job_analysis?.weaknesses?.length ? (
                        <ul className="list-disc list-inside space-y-1">
                          {selectedScore.job_analysis.weaknesses.map((w, i) => (
                            <li key={i}>{w.substring(0, 100)}{w.length > 100 ? '...' : ''}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500">No weaknesses listed</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end">
                <Button onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminJobMatchQuality;
